import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { useEffect, useMemo, useState } from "react";
import {
  Target, Plus, Search, Filter, MoreVertical, Copy, Trash2, Edit,
  BarChart3, Calendar, DollarSign, TrendingUp, Clock, CheckCircle2,
  Pause, Play, Archive, AlertCircle, FileText, Flag, StickyNote,
  ArrowUpDown, ChevronDown, Users, Zap, Eye, MousePointer, X, ShieldCheck, ShieldAlert, Sparkles, AlertTriangle, Layers, Gauge, Save, Star,
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, Legend } from "recharts";

const STATUS_COLORS: Record<string, string> = {
  draft: "bg-gray-100 text-gray-700",
  active: "bg-green-100 text-green-700",
  paused: "bg-yellow-100 text-yellow-700",
  completed: "bg-blue-100 text-blue-700",
  archived: "bg-red-100 text-red-700",
};
const STATUS_ICONS: Record<string, any> = {
  draft: FileText, active: Play, paused: Pause, completed: CheckCircle2, archived: Archive,
};
const TYPE_LABELS: Record<string, string> = {
  multi: "Multi-Channel", social: "Social Media", email: "Email", seo: "SEO", ppc: "PPC/Ads", content: "Content", ads: "Ads",
};
const PIE_COLORS = ["#8B5CF6", "#EC4899", "#F59E0B", "#10B981", "#6366F1", "#EF4444"];
const GRADIENT = "bg-gradient-to-r from-violet-500 via-purple-500 to-pink-500";

const tabs = [
  { id: "overview", label: "Overview", icon: BarChart3 },
  { id: "campaigns", label: "All Campaigns", icon: Target },
  { id: "calendar", label: "Calendar", icon: Calendar },
  { id: "budget", label: "Budget", icon: DollarSign },
  { id: "performance", label: "Performance", icon: TrendingUp },
];

export default function Campaigns() {
  const qc = useQueryClient();
  const [tab, setTab] = useState("overview");
  const [showCreate, setShowCreate] = useState(false);
  const [showDetail, setShowDetail] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<string[]>([]);
  const [sort, setSort] = useState("updatedAt");
  const [order, setOrder] = useState<"asc" | "desc">("desc");
  const [newViewName, setNewViewName] = useState("");
  const [newViewDefault, setNewViewDefault] = useState(false);
  const [goalDraft, setGoalDraft] = useState({ impressions: "", clicks: "", conversions: "", revenue: "" });

  const { data: stats } = useQuery({ queryKey: ["campaign-stats"], queryFn: () => api.get<any>("/campaigns/stats") });
  const { data: list, isLoading } = useQuery({
    queryKey: ["campaigns", statusFilter, typeFilter, search, page, sort, order],
    queryFn: () => api.get<any>(`/campaigns?page=${page}&limit=20${statusFilter ? `&status=${statusFilter}` : ""}${typeFilter ? `&type=${typeFilter}` : ""}${search ? `&search=${search}` : ""}${sort ? `&sort=${sort}` : ""}${order ? `&order=${order}` : ""}`),
  });
  const { data: detail } = useQuery({
    queryKey: ["campaign-detail", showDetail],
    queryFn: () => api.get<any>(`/campaigns/${showDetail}`),
    enabled: !!showDetail,
  });
  const { data: calendarData } = useQuery({
    queryKey: ["campaign-calendar"],
    queryFn: () => api.get<any>("/campaigns/calendar"),
    enabled: tab === "calendar",
  });
  const { data: savedViewsData } = useQuery({
    queryKey: ["campaign-saved-views"],
    queryFn: () => api.get<any>("/campaigns/views"),
    enabled: tab === "campaigns",
  });
  const compareEnabled = selected.length >= 2 && selected.length <= 5;
  const { data: comparisonData, isFetching: comparing } = useQuery({
    queryKey: ["campaign-comparison", [...selected].sort().join(",")],
    queryFn: () => api.post<any>("/campaigns/comparison", { ids: selected }),
    enabled: tab === "campaigns" && compareEnabled,
  });

  const createMut = useMutation({ mutationFn: (b: any) => api.post("/campaigns", b), onSuccess: () => { qc.invalidateQueries({ queryKey: ["campaigns"] }); qc.invalidateQueries({ queryKey: ["campaign-stats"] }); setShowCreate(false); setForm({ name: "", description: "", type: "multi", budget: "", startDate: "", endDate: "", tags: "" }); } });
  const deleteMut = useMutation({ mutationFn: (id: string) => api.delete(`/campaigns/${id}`), onSuccess: () => { qc.invalidateQueries({ queryKey: ["campaigns"] }); qc.invalidateQueries({ queryKey: ["campaign-stats"] }); } });
  const dupMut = useMutation({ mutationFn: (id: string) => api.post(`/campaigns/${id}/duplicate`), onSuccess: () => { qc.invalidateQueries({ queryKey: ["campaigns"] }); qc.invalidateQueries({ queryKey: ["campaign-stats"] }); } });
  const bulkStatusMut = useMutation({ mutationFn: (b: any) => api.post("/campaigns/bulk/status", b), onSuccess: () => { setSelected([]); qc.invalidateQueries({ queryKey: ["campaigns"] }); qc.invalidateQueries({ queryKey: ["campaign-stats"] }); } });
  const bulkDeleteMut = useMutation({ mutationFn: (b: any) => api.post("/campaigns/bulk/delete", b), onSuccess: () => { setSelected([]); qc.invalidateQueries({ queryKey: ["campaigns"] }); qc.invalidateQueries({ queryKey: ["campaign-stats"] }); } });
  const addNoteMut = useMutation({ mutationFn: ({ id, ...b }: any) => api.post(`/campaigns/${id}/notes`, b), onSuccess: () => { qc.invalidateQueries({ queryKey: ["campaign-detail"] }); setNoteText(""); } });
  const addMilestoneMut = useMutation({ mutationFn: ({ id, ...b }: any) => api.post(`/campaigns/${id}/milestones`, b), onSuccess: () => { qc.invalidateQueries({ queryKey: ["campaign-detail"] }); setMilestoneTitle(""); } });
  const toggleMilestoneMut = useMutation({ mutationFn: ({ cid, mid, completed }: any) => api.put(`/campaigns/${cid}/milestones/${mid}`, { completed }), onSuccess: () => qc.invalidateQueries({ queryKey: ["campaign-detail"] }) });
  const updateMut = useMutation({ mutationFn: ({ id, ...b }: any) => api.put(`/campaigns/${id}`, b), onSuccess: () => { qc.invalidateQueries({ queryKey: ["campaigns"] }); qc.invalidateQueries({ queryKey: ["campaign-stats"] }); qc.invalidateQueries({ queryKey: ["campaign-detail"] }); } });
  const saveViewMut = useMutation({
    mutationFn: (body: any) => api.post("/campaigns/views", body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["campaign-saved-views"] });
      setNewViewName("");
      setNewViewDefault(false);
    },
  });
  const updateViewMut = useMutation({
    mutationFn: ({ id, ...body }: any) => api.put(`/campaigns/views/${id}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["campaign-saved-views"] }),
  });
  const deleteViewMut = useMutation({
    mutationFn: (id: string) => api.delete(`/campaigns/views/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["campaign-saved-views"] }),
  });

  const [form, setForm] = useState({ name: "", description: "", type: "multi", budget: "", startDate: "", endDate: "", tags: "" });
  const [noteText, setNoteText] = useState("");
  const [milestoneTitle, setMilestoneTitle] = useState("");

  const campaigns = list?.campaigns || [];
  const s = stats || {} as any;
  const savedViews = savedViewsData?.views || [];
  const activeFilters = { statusFilter, typeFilter, search, sort, order };

  useEffect(() => {
    if (!detail) return;
    const g = detail.goals || {};
    setGoalDraft({
      impressions: g.impressions ? String(g.impressions) : "",
      clicks: g.clicks ? String(g.clicks) : "",
      conversions: g.conversions ? String(g.conversions) : "",
      revenue: g.revenue ? String(g.revenue) : "",
    });
  }, [detail?.id, detail?.goals]);

  // ─── Helpers ──────────────────────────
  const KPI = ({ icon: Icon, label, value, sub, color }: any) => (
    <div className="card p-5 hover:shadow-md transition-shadow">
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${color}`}><Icon className="w-5 h-5 text-white" /></div>
        <div><p className="text-2xl font-bold text-gray-900">{value}</p><p className="text-xs text-gray-500">{label}</p></div>
      </div>
      {sub && <p className="text-xs text-gray-400 mt-2">{sub}</p>}
    </div>
  );

  const toggleSelect = (id: string) => setSelected((p) => {
    if (p.includes(id)) return p.filter((x) => x !== id);
    if (p.length >= 5) return p;
    return [...p, id];
  });
  const toggleAll = () => {
    const pageIds = campaigns.map((c: any) => c.id);
    const capped = pageIds.slice(0, 5);
    const allCappedSelected = capped.length > 0 && capped.every((id: string) => selected.includes(id));
    setSelected(allCappedSelected ? selected.filter((id) => !capped.includes(id)) : Array.from(new Set([...selected, ...capped])).slice(0, 5));
  };

  const budgetPct = (spent: number, budget: number) => budget > 0 ? Math.min(100, Math.round((spent / budget) * 100)) : 0;

  // ─── Type distribution pie data ──────
  const typeData = s.byType ? Object.entries(s.byType).map(([name, value]) => ({ name: TYPE_LABELS[name] || name, value })) : [];
  const monthlyData = s.monthly ? Object.entries(s.monthly).sort(([a], [b]) => a.localeCompare(b)).map(([month, count]) => ({ month, count })) : [];
  const comparison = comparisonData?.comparison || [];
  const monthlyGrowthPct = monthlyData.length >= 2
    ? (() => {
        const prev = Number(monthlyData[monthlyData.length - 2]?.count || 0);
        const curr = Number(monthlyData[monthlyData.length - 1]?.count || 0);
        if (prev === 0) return curr > 0 ? 100 : 0;
        return Math.round(((curr - prev) / prev) * 100);
      })()
    : 0;
  const portfolioInsights = useMemo(() => {
    const completionRate = s.total > 0 ? Math.round((s.completed / s.total) * 100) : 0;
    const budgetUtilization = Number(s.budgetUtilization || 0);
    const activeLoad = Number(s.active || 0);
    const healthScore = Math.max(0, Math.min(100, Math.round(
      (completionRate * 0.35) + ((100 - budgetUtilization) * 0.35) + (Math.min(activeLoad * 12, 100) * 0.3)
    )));

    const now = Date.now();
    const riskCampaigns = campaigns.filter((c: any) => {
      const highBurn = budgetPct(c.spent || 0, c.budget || 0) >= 85;
      const end = c.endDate ? new Date(c.endDate).getTime() : null;
      const dueSoon = end ? end > now && (end - now) / 86400000 <= 7 && c.status !== "completed" : false;
      return highBurn || dueSoon;
    });

    return {
      completionRate,
      budgetUtilization,
      healthScore,
      riskCount: riskCampaigns.length,
      riskCampaigns: riskCampaigns.slice(0, 4),
    };
  }, [campaigns, s]);
  const comparisonBudgetData = comparison.length
    ? comparison.map((c: any) => ({ name: c.name.length > 16 ? `${c.name.slice(0, 16)}…` : c.name, budget: c.budget || 0, spent: c.spent || 0 }))
    : [];
  const campaignOps = useMemo(() => {
    const activeCampaigns = campaigns.filter((c: any) => c.status === "active");
    const avgBurnRate = campaigns.length
      ? Math.round(campaigns.reduce((sum: number, c: any) => sum + budgetPct(c.spent || 0, c.budget || 0), 0) / campaigns.length)
      : 0;
    const highRiskCount = campaigns.filter((c: any) => budgetPct(c.spent || 0, c.budget || 0) >= 90).length;
    const now = Date.now();
    const endingSoon = campaigns.filter((c: any) => {
      if (!c.endDate || c.status === "completed" || c.status === "archived") return false;
      const left = Math.ceil((new Date(c.endDate).getTime() - now) / 86400000);
      return left >= 0 && left <= 7;
    }).length;
    const deliveryIndex = Math.max(0, Math.min(100, Math.round(
      (Math.min(activeCampaigns.length * 25, 100) * 0.4) +
      ((100 - avgBurnRate) * 0.35) +
      ((Math.min((s.completed || 0) * 20, 100)) * 0.25)
    )));
    return {
      activeCampaigns: activeCampaigns.length,
      avgBurnRate,
      highRiskCount,
      endingSoon,
      deliveryIndex,
    };
  }, [campaigns, s]);
  const comparisonSummary = useMemo(() => {
    if (!comparison.length) return null;
    const totalBudget = comparison.reduce((sum: number, c: any) => sum + Number(c.budget || 0), 0);
    const totalSpent = comparison.reduce((sum: number, c: any) => sum + Number(c.spent || 0), 0);
    const avgRoi = comparison.length
      ? Math.round(comparison.reduce((sum: number, c: any) => sum + Number(c.roi || 0), 0) / comparison.length)
      : 0;
    const leader = [...comparison].sort((a: any, b: any) => (b.roi || 0) - (a.roi || 0))[0];
    return { totalBudget, totalSpent, avgRoi, leader };
  }, [comparison]);
  const applySavedView = (view: any) => {
    const filters = view?.filters || {};
    setStatusFilter(filters.statusFilter || "");
    setTypeFilter(filters.typeFilter || "");
    setSearch(filters.search || "");
    setSort(filters.sort || "updatedAt");
    setOrder(filters.order === "asc" ? "asc" : "desc");
    setPage(1);
  };
  const isViewActive = (view: any) => {
    const filters = view?.filters || {};
    return (
      (filters.statusFilter || "") === statusFilter &&
      (filters.typeFilter || "") === typeFilter &&
      (filters.search || "") === search &&
      (filters.sort || "updatedAt") === sort &&
      (filters.order === "asc" ? "asc" : "desc") === order
    );
  };
  const saveGoalTargets = (campaignId: string) => {
    const payload: Record<string, number> = {};
    if (goalDraft.impressions) payload.impressions = Number(goalDraft.impressions);
    if (goalDraft.clicks) payload.clicks = Number(goalDraft.clicks);
    if (goalDraft.conversions) payload.conversions = Number(goalDraft.conversions);
    if (goalDraft.revenue) payload.revenue = Number(goalDraft.revenue);
    updateMut.mutate({ id: campaignId, goals: Object.keys(payload).length ? payload : null });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2"><Target className="w-6 h-6 text-purple-500" /> Campaigns</h1>
          <p className="text-sm text-gray-500 mt-1">{s.total || 0} campaigns · {s.active || 0} active · ${(s.totalBudget || 0).toLocaleString()} total budget</p>
        </div>
        <button onClick={() => setShowCreate(true)} className={`${GRADIENT} text-white px-4 py-2 rounded-xl text-sm font-medium flex items-center gap-2 hover:opacity-90 transition`}>
          <Plus className="w-4 h-4" /> New Campaign
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-xl">
        {tabs.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${tab === t.id ? "bg-white text-purple-700 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}>
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>

      {/* ════════ OVERVIEW TAB ════════ */}
      {tab === "overview" && (
        <div className="space-y-6">
          {/* KPI Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <KPI icon={Target} label="Total Campaigns" value={s.total || 0} color="bg-purple-500" sub={`${s.runningNow || 0} running now`} />
            <KPI icon={Play} label="Active" value={s.active || 0} color="bg-green-500" sub={`${s.paused || 0} paused`} />
            <KPI icon={DollarSign} label="Total Budget" value={`$${(s.totalBudget || 0).toLocaleString()}`} color="bg-blue-500" sub={`${s.budgetUtilization || 0}% utilized`} />
            <KPI icon={TrendingUp} label="Total Spent" value={`$${(s.totalSpent || 0).toLocaleString()}`} color="bg-pink-500" sub={`$${(s.remaining || 0).toLocaleString()} remaining`} />
          </div>

          {/* Enterprise Command Center */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="card p-5 bg-gradient-to-br from-indigo-50 via-white to-purple-50">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                  <ShieldCheck className="w-4 h-4 text-indigo-600" />
                  Portfolio Health
                </h3>
                <span className="text-xs font-semibold px-2 py-1 rounded-full bg-indigo-100 text-indigo-700">Enterprise</span>
              </div>
              <div className="flex items-end gap-2">
                <p className="text-3xl font-bold text-gray-900">{portfolioInsights.healthScore}</p>
                <p className="text-xs text-gray-500 mb-1">/100</p>
              </div>
              <div className="mt-3 h-2 w-full rounded-full bg-gray-100 overflow-hidden">
                <div className="h-full rounded-full bg-gradient-to-r from-indigo-500 to-purple-500" style={{ width: `${portfolioInsights.healthScore}%` }} />
              </div>
              <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                <p className="text-gray-500">Completion: <span className="font-semibold text-gray-700">{portfolioInsights.completionRate}%</span></p>
                <p className="text-gray-500">Utilization: <span className="font-semibold text-gray-700">{portfolioInsights.budgetUtilization}%</span></p>
              </div>
            </div>

            <div className="card p-5">
              <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2 mb-3">
                <ShieldAlert className="w-4 h-4 text-amber-600" />
                Risk Radar
              </h3>
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm text-gray-500">At-risk campaigns</p>
                <span className={`text-sm font-semibold ${portfolioInsights.riskCount > 0 ? "text-amber-600" : "text-green-600"}`}>
                  {portfolioInsights.riskCount}
                </span>
              </div>
              <div className="space-y-2">
                {portfolioInsights.riskCampaigns.length > 0 ? portfolioInsights.riskCampaigns.map((c: any) => (
                  <button
                    key={c.id}
                    onClick={() => setShowDetail(c.id)}
                    className="w-full flex items-center justify-between rounded-lg border border-amber-100 bg-amber-50/70 px-3 py-2 text-left hover:bg-amber-100/60 transition-colors"
                  >
                    <span className="text-xs font-medium text-amber-800 truncate max-w-[190px]">{c.name}</span>
                    <span className="text-[11px] text-amber-700">{budgetPct(c.spent || 0, c.budget || 0)}%</span>
                  </button>
                )) : (
                  <div className="rounded-lg border border-green-100 bg-green-50 px-3 py-2 text-xs text-green-700">
                    No immediate portfolio risks detected.
                  </div>
                )}
              </div>
            </div>

            <div className="card p-5">
              <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2 mb-3">
                <Sparkles className="w-4 h-4 text-purple-600" />
                Growth Signals
              </h3>
              <div className="space-y-3">
                <div className="rounded-lg border border-gray-100 bg-gray-50 px-3 py-2">
                  <p className="text-xs text-gray-500">Monthly creation trend</p>
                  <p className={`text-sm font-semibold ${monthlyGrowthPct >= 0 ? "text-green-600" : "text-red-600"}`}>
                    {monthlyGrowthPct >= 0 ? "+" : ""}{monthlyGrowthPct}% vs prior month
                  </p>
                </div>
                <div className="rounded-lg border border-gray-100 bg-gray-50 px-3 py-2">
                  <p className="text-xs text-gray-500">Operating posture</p>
                  <p className="text-sm font-semibold text-gray-800">
                    {portfolioInsights.budgetUtilization > 85 ? "High burn pressure" : portfolioInsights.completionRate < 40 ? "Execution ramp-up" : "Balanced portfolio"}
                  </p>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => { setTab("campaigns"); setStatusFilter("active"); }} className="text-xs px-3 py-1.5 rounded-lg bg-purple-100 text-purple-700 hover:bg-purple-200 transition-colors">
                    Focus Active
                  </button>
                  <button onClick={() => { setTab("budget"); }} className="text-xs px-3 py-1.5 rounded-lg bg-blue-100 text-blue-700 hover:bg-blue-200 transition-colors">
                    Budget Ops
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Status summary cards */}
          <div className="grid grid-cols-5 gap-3">
            {[
              { status: "draft", count: s.draft, color: "border-gray-300 bg-gray-50" },
              { status: "active", count: s.active, color: "border-green-300 bg-green-50" },
              { status: "paused", count: s.paused, color: "border-yellow-300 bg-yellow-50" },
              { status: "completed", count: s.completed, color: "border-blue-300 bg-blue-50" },
              { status: "archived", count: s.archived, color: "border-red-300 bg-red-50" },
            ].map((item) => {
              const Ic = STATUS_ICONS[item.status];
              return (
                <button key={item.status} onClick={() => { setStatusFilter(item.status); setTab("campaigns"); }}
                  className={`p-4 rounded-xl border-2 ${item.color} hover:shadow-md transition-all text-center cursor-pointer`}>
                  <Ic className="w-5 h-5 mx-auto mb-1 text-gray-600" />
                  <p className="text-xl font-bold text-gray-900">{item.count || 0}</p>
                  <p className="text-xs text-gray-500 capitalize">{item.status}</p>
                </button>
              );
            })}
          </div>

          {/* Charts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Type Distribution */}
            <div className="card p-6">
              <h3 className="font-semibold text-gray-900 mb-4">Campaign Types</h3>
              {typeData.length > 0 ? (
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie data={typeData} cx="50%" cy="50%" outerRadius={80} label={({ name, value }: any) => `${name}: ${value}`} dataKey="value">
                      {typeData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              ) : <p className="text-sm text-gray-400 py-12 text-center">No campaigns yet</p>}
            </div>

            {/* Monthly Trend */}
            <div className="card p-6">
              <h3 className="font-semibold text-gray-900 mb-4">Monthly Trend</h3>
              {monthlyData.length > 0 ? (
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={monthlyData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Bar dataKey="count" fill="#8B5CF6" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : <p className="text-sm text-gray-400 py-12 text-center">No data yet</p>}
            </div>
          </div>

          {/* Recent Campaigns */}
          <div className="card p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-900">Recent Campaigns</h3>
              <button onClick={() => setTab("campaigns")} className="text-sm text-purple-600 hover:text-purple-700 font-medium">View All →</button>
            </div>
            {campaigns.length > 0 ? (
              <div className="space-y-3">
                {campaigns.slice(0, 5).map((c: any) => (
                  <div key={c.id} onClick={() => setShowDetail(c.id)} className="flex items-center justify-between p-3 rounded-xl hover:bg-gray-50 cursor-pointer transition-colors">
                    <div className="flex items-center gap-3">
                      <div className={`w-2.5 h-2.5 rounded-full ${c.status === "active" ? "bg-green-500" : c.status === "paused" ? "bg-yellow-500" : c.status === "completed" ? "bg-blue-500" : "bg-gray-400"}`} />
                      <div>
                        <p className="text-sm font-medium text-gray-900">{c.name}</p>
                        <p className="text-xs text-gray-400">{TYPE_LABELS[c.type] || c.type} · {c._count?.contents || 0} content items</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="text-sm font-medium text-gray-700">${c.budget?.toFixed(0) || "0"}</p>
                        <div className="w-20 h-1.5 bg-gray-200 rounded-full mt-1">
                          <div className="h-full bg-purple-500 rounded-full" style={{ width: `${budgetPct(c.spent || 0, c.budget || 0)}%` }} />
                        </div>
                      </div>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[c.status] || "bg-gray-100 text-gray-700"}`}>{c.status}</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Target className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-gray-400">No campaigns yet</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ════════ ALL CAMPAIGNS TAB ════════ */}
      {tab === "campaigns" && (
        <div className="space-y-4">
          {/* Ops strip */}
          <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
            <div className="card p-4 border border-indigo-100 bg-gradient-to-br from-indigo-50 via-white to-purple-50">
              <p className="text-xs text-gray-500 flex items-center gap-1"><Gauge className="w-3.5 h-3.5 text-indigo-500" /> Delivery Index</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{campaignOps.deliveryIndex}</p>
              <p className="text-[11px] text-gray-500">Active mix and burn control</p>
            </div>
            <div className="card p-4">
              <p className="text-xs text-gray-500 flex items-center gap-1"><Layers className="w-3.5 h-3.5 text-purple-500" /> Active Programs</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{campaignOps.activeCampaigns}</p>
              <p className="text-[11px] text-gray-500">{campaignOps.endingSoon} ending within 7 days</p>
            </div>
            <div className="card p-4">
              <p className="text-xs text-gray-500 flex items-center gap-1"><TrendingUp className="w-3.5 h-3.5 text-blue-500" /> Avg Burn Rate</p>
              <p className={`text-2xl font-bold mt-1 ${campaignOps.avgBurnRate >= 80 ? "text-red-600" : campaignOps.avgBurnRate >= 60 ? "text-amber-600" : "text-gray-900"}`}>
                {campaignOps.avgBurnRate}%
              </p>
              <p className="text-[11px] text-gray-500">Budget consumed across loaded campaigns</p>
            </div>
            <div className="card p-4">
              <p className="text-xs text-gray-500 flex items-center gap-1"><ShieldAlert className="w-3.5 h-3.5 text-amber-500" /> Risk Signals</p>
              <p className={`text-2xl font-bold mt-1 ${campaignOps.highRiskCount > 0 ? "text-amber-600" : "text-green-600"}`}>{campaignOps.highRiskCount}</p>
              <p className="text-[11px] text-gray-500">Campaigns at 90%+ spend</p>
            </div>
          </div>

          {/* Filters & controls */}
          <div className="flex items-center gap-3 flex-wrap">
            <div className="relative flex-1 min-w-[240px] max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input type="text" placeholder="Search campaigns..." className="input pl-10" value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} />
            </div>
            <div className="flex items-center gap-1">
              {["", "draft", "active", "paused", "completed", "archived"].map((st) => (
                <button key={st} onClick={() => { setStatusFilter(st); setPage(1); }}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${statusFilter === st ? "bg-purple-100 text-purple-700" : "text-gray-500 hover:bg-gray-100"}`}>
                  {st || "All"}
                </button>
              ))}
            </div>
            <select className="input w-36 text-xs" value={typeFilter} onChange={(e) => { setTypeFilter(e.target.value); setPage(1); }}>
              <option value="">All Types</option>
              {Object.entries(TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            <select className="input w-40 text-xs" value={sort} onChange={(e) => { setSort(e.target.value); setPage(1); }}>
              <option value="updatedAt">Sort: Last Updated</option>
              <option value="createdAt">Sort: Created Date</option>
              <option value="name">Sort: Campaign Name</option>
              <option value="budget">Sort: Budget</option>
              <option value="spent">Sort: Spend</option>
              <option value="startDate">Sort: Start Date</option>
            </select>
            <button
              onClick={() => { setOrder(order === "desc" ? "asc" : "desc"); setPage(1); }}
              className="px-3 py-2 rounded-lg border border-gray-200 text-xs font-medium text-gray-600 hover:bg-gray-50 transition-colors flex items-center gap-1"
              title="Toggle sort direction"
            >
              <ArrowUpDown className="w-3.5 h-3.5" />
              {order === "desc" ? "Descending" : "Ascending"}
            </button>
          </div>

          {/* Saved views */}
          <div className="card p-4 space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                <Save className="w-4 h-4 text-purple-600" />
                Saved Views
              </h3>
              <span className="text-xs text-gray-500">{savedViews.length} saved</span>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {savedViews.length > 0 ? savedViews.map((view: any) => (
                <div key={view.id} className={`inline-flex items-center rounded-lg border px-2 py-1 ${isViewActive(view) ? "border-purple-300 bg-purple-50" : "border-gray-200 bg-white"}`}>
                  <button onClick={() => applySavedView(view)} className={`text-xs font-medium ${isViewActive(view) ? "text-purple-700" : "text-gray-700 hover:text-purple-700"}`}>
                    {view.name}
                  </button>
                  {view.isDefault && <Star className="w-3 h-3 ml-1 text-amber-500 fill-amber-400" />}
                  {!view.isDefault && (
                    <button
                      onClick={() => updateViewMut.mutate({ id: view.id, isDefault: true })}
                      className="ml-1 text-[10px] text-gray-400 hover:text-amber-500"
                      title="Set default"
                    >
                      default
                    </button>
                  )}
                  <button
                    onClick={() => { if (confirm(`Delete saved view "${view.name}"?`)) deleteViewMut.mutate(view.id); }}
                    className="ml-2 text-gray-400 hover:text-red-600"
                    title="Delete view"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              )) : (
                <p className="text-xs text-gray-400">No saved views yet. Save your current filter stack below.</p>
              )}
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <input
                className="input h-9 text-xs w-52"
                placeholder="View name (e.g. Q2 Active)"
                value={newViewName}
                onChange={(e) => setNewViewName(e.target.value)}
              />
              <label className="flex items-center gap-1 text-xs text-gray-500">
                <input type="checkbox" checked={newViewDefault} onChange={(e) => setNewViewDefault(e.target.checked)} className="rounded border-gray-300" />
                set as default
              </label>
              <button
                onClick={() => saveViewMut.mutate({ name: newViewName.trim(), filters: activeFilters, isDefault: newViewDefault })}
                disabled={!newViewName.trim() || saveViewMut.isPending}
                className="px-3 py-1.5 rounded-lg text-xs font-medium bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-50"
              >
                {saveViewMut.isPending ? "Saving..." : "Save Current View"}
              </button>
            </div>
          </div>

          {/* Bulk Actions */}
          {selected.length > 0 && (
            <div className="flex items-center gap-3 p-3 bg-purple-50 rounded-xl border border-purple-200">
              <span className="text-sm font-medium text-purple-700">{selected.length} selected (max 5 for compare)</span>
              <div className="flex gap-2 ml-auto">
                {["active", "paused", "completed", "archived"].map((st) => (
                  <button key={st} onClick={() => bulkStatusMut.mutate({ ids: selected, status: st })}
                    className="px-3 py-1 rounded-lg text-xs font-medium bg-white border border-gray-200 hover:bg-gray-50 capitalize transition-colors">{st}</button>
                ))}
                <button onClick={() => { if (confirm(`Delete ${selected.length} campaigns?`)) bulkDeleteMut.mutate({ ids: selected }); }}
                  className="px-3 py-1 rounded-lg text-xs font-medium bg-red-50 text-red-600 border border-red-200 hover:bg-red-100 transition-colors">Delete</button>
                <button onClick={() => setSelected([])} className="px-3 py-1 rounded-lg text-xs text-gray-500 hover:bg-gray-100 transition-colors">Clear</button>
              </div>
            </div>
          )}

          {/* Comparison workspace */}
          {compareEnabled ? (
            <div className="card p-5 space-y-4">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-purple-600" />
                    Enterprise Comparison Workspace
                  </h3>
                  <p className="text-xs text-gray-500 mt-1">Cross-campaign benchmark for selected programs</p>
                </div>
                <button onClick={() => setSelected([])} className="text-xs text-gray-500 hover:text-gray-700">Reset selection</button>
              </div>
              {comparing ? (
                <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600" /></div>
              ) : comparisonSummary ? (
                <div className="grid grid-cols-1 xl:grid-cols-5 gap-4">
                  <div className="xl:col-span-3 space-y-4">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <div className="rounded-xl border border-gray-100 bg-gray-50 px-3 py-2">
                        <p className="text-[11px] text-gray-500">Portfolio Budget</p>
                        <p className="text-sm font-semibold text-gray-900">${comparisonSummary.totalBudget.toLocaleString()}</p>
                      </div>
                      <div className="rounded-xl border border-gray-100 bg-gray-50 px-3 py-2">
                        <p className="text-[11px] text-gray-500">Portfolio Spend</p>
                        <p className="text-sm font-semibold text-gray-900">${comparisonSummary.totalSpent.toLocaleString()}</p>
                      </div>
                      <div className="rounded-xl border border-gray-100 bg-gray-50 px-3 py-2">
                        <p className="text-[11px] text-gray-500">Avg ROI</p>
                        <p className={`text-sm font-semibold ${comparisonSummary.avgRoi >= 0 ? "text-green-600" : "text-red-600"}`}>{comparisonSummary.avgRoi}%</p>
                      </div>
                      <div className="rounded-xl border border-gray-100 bg-gray-50 px-3 py-2">
                        <p className="text-[11px] text-gray-500">Top Performer</p>
                        <p className="text-sm font-semibold text-gray-900 truncate">{comparisonSummary.leader?.name || "—"}</p>
                      </div>
                    </div>
                    <div className="h-52 rounded-xl border border-gray-100 p-3">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={comparisonBudgetData}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                          <YAxis tick={{ fontSize: 11 }} />
                          <Tooltip />
                          <Legend />
                          <Bar dataKey="budget" fill="#8B5CF6" radius={[4, 4, 0, 0]} />
                          <Bar dataKey="spent" fill="#EC4899" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                  <div className="xl:col-span-2 rounded-xl border border-gray-100 bg-gray-50/70 p-3">
                    <p className="text-xs font-semibold text-gray-700 mb-2">Campaign Signals</p>
                    <div className="space-y-2 max-h-56 overflow-y-auto">
                      {comparison.map((cmp: any) => (
                        <button key={cmp.id} onClick={() => setShowDetail(cmp.id)} className="w-full text-left rounded-lg bg-white border border-gray-100 px-3 py-2 hover:border-purple-200 transition-colors">
                          <p className="text-xs font-medium text-gray-800 truncate">{cmp.name}</p>
                          <div className="mt-1 flex items-center justify-between text-[11px] text-gray-500">
                            <span>{TYPE_LABELS[cmp.type] || cmp.type}</span>
                            <span className={cmp.roi >= 0 ? "text-green-600" : "text-red-600"}>{cmp.roi}% ROI</span>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              ) : null}
            </div>
          ) : (
            <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 px-4 py-3 flex items-center justify-between">
              <p className="text-xs text-gray-500">
                Select 2-5 campaigns to unlock enterprise comparison workspace.
              </p>
              <span className="text-xs text-gray-400">{selected.length}/5 selected</span>
            </div>
          )}

          {/* Table */}
          {isLoading ? (
            <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600" /></div>
          ) : campaigns.length > 0 ? (
            <div className="card overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={campaigns.slice(0, 5).length > 0 && campaigns.slice(0, 5).every((c: any) => selected.includes(c.id))}
                        onChange={toggleAll}
                        className="rounded border-gray-300"
                        title="Select up to 5 campaigns"
                      />
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Campaign</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Type</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Budget</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Progress</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Signals</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Content</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Updated</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {campaigns.map((c: any) => {
                    const pct = budgetPct(c.spent || 0, c.budget || 0);
                    const daysLeft = c.endDate ? Math.ceil((new Date(c.endDate).getTime() - Date.now()) / 86400000) : null;
                    const overspend = pct >= 90;
                    const dueSoon = daysLeft !== null && daysLeft >= 0 && daysLeft <= 7 && c.status !== "completed" && c.status !== "archived";
                    return (
                      <tr key={c.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-3"><input type="checkbox" checked={selected.includes(c.id)} onChange={() => toggleSelect(c.id)} className="rounded border-gray-300" /></td>
                        <td className="px-4 py-3 cursor-pointer" onClick={() => setShowDetail(c.id)}>
                          <p className="text-sm font-medium text-gray-900">{c.name}</p>
                          {c.description && <p className="text-xs text-gray-400 mt-0.5 truncate max-w-xs">{c.description}</p>}
                        </td>
                        <td className="px-4 py-3"><span className="bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full text-xs font-medium">{TYPE_LABELS[c.type] || c.type}</span></td>
                        <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[c.status] || "bg-gray-100 text-gray-700"}`}>{c.status}</span></td>
                        <td className="px-4 py-3">
                          <p className="text-sm text-gray-700">${c.budget?.toFixed(0) || "0"}</p>
                          <div className="w-16 h-1 bg-gray-200 rounded-full mt-1"><div className="h-full bg-purple-500 rounded-full transition-all" style={{ width: `${pct}%` }} /></div>
                        </td>
                        <td className="px-4 py-3"><span className="text-xs text-gray-500">{pct}% spent</span></td>
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap gap-1">
                            {overspend && <span className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-red-100 text-red-700">High burn</span>}
                            {dueSoon && <span className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-amber-100 text-amber-700">Due soon</span>}
                            {!overspend && !dueSoon && <span className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-green-100 text-green-700">Stable</span>}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2 text-xs text-gray-500">
                            <span>{c._count?.contents || 0} items</span>
                            <span>· {c._count?.scheduledPosts || 0} posts</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-400">{new Date(c.updatedAt).toLocaleDateString()}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                            <button onClick={() => setShowDetail(c.id)} className="p-1 text-gray-400 hover:text-purple-600" title="View"><Eye className="w-4 h-4" /></button>
                            <button onClick={() => dupMut.mutate(c.id)} className="p-1 text-gray-400 hover:text-green-600" title="Duplicate"><Copy className="w-4 h-4" /></button>
                            <button onClick={() => { if (confirm("Delete?")) deleteMut.mutate(c.id); }} className="p-1 text-gray-400 hover:text-red-600" title="Delete"><Trash2 className="w-4 h-4" /></button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {list?.pages > 1 && (
                <div className="flex items-center justify-between px-6 py-3 border-t border-gray-100">
                  <p className="text-xs text-gray-500">Page {list.page} of {list.pages} · {list.total} campaigns</p>
                  <div className="flex gap-2">
                    <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page === 1} className="px-3 py-1 rounded-lg text-xs border border-gray-200 hover:bg-gray-50 disabled:opacity-50">Previous</button>
                    <button onClick={() => setPage(Math.min(list.pages, page + 1))} disabled={page >= list.pages} className="px-3 py-1 rounded-lg text-xs border border-gray-200 hover:bg-gray-50 disabled:opacity-50">Next</button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="card p-12 text-center">
              <div className={`w-16 h-16 ${GRADIENT} rounded-2xl flex items-center justify-center mx-auto mb-4`}>
                <Target className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-1">No campaigns yet</h3>
              <p className="text-sm text-gray-500 mb-4">Create your first campaign to start marketing across all platforms.</p>
              <button onClick={() => setShowCreate(true)} className={`${GRADIENT} text-white px-4 py-2 rounded-xl text-sm font-medium hover:opacity-90`}>Create Campaign</button>
            </div>
          )}
        </div>
      )}

      {/* ════════ CALENDAR TAB ════════ */}
      {tab === "calendar" && (
        <div className="space-y-4">
          <div className="card p-6">
            <h3 className="font-semibold text-gray-900 mb-4">Campaign Timeline</h3>
            {calendarData?.campaigns?.length > 0 ? (
              <div className="space-y-3">
                {calendarData.campaigns.map((c: any) => {
                  const start = c.startDate ? new Date(c.startDate) : null;
                  const end = c.endDate ? new Date(c.endDate) : null;
                  const pct = budgetPct(c.spent || 0, c.budget || 0);
                  return (
                    <div key={c.id} onClick={() => setShowDetail(c.id)} className="flex items-center gap-4 p-4 rounded-xl border border-gray-200 hover:border-purple-300 hover:shadow-sm cursor-pointer transition-all">
                      <div className={`w-2 h-12 rounded-full ${c.status === "active" ? "bg-green-500" : c.status === "paused" ? "bg-yellow-500" : c.status === "completed" ? "bg-blue-500" : "bg-gray-300"}`} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900">{c.name}</p>
                        <div className="flex items-center gap-3 mt-1 text-xs text-gray-400">
                          <span className={`px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[c.status]}`}>{c.status}</span>
                          <span>{TYPE_LABELS[c.type] || c.type}</span>
                          <span>{c._count?.contents || 0} content · {c._count?.scheduledPosts || 0} posts</span>
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <div className="flex items-center gap-2 text-xs text-gray-500">
                          <Calendar className="w-3.5 h-3.5" />
                          {start ? start.toLocaleDateString() : "No start"} → {end ? end.toLocaleDateString() : "No end"}
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          <div className="w-24 h-1.5 bg-gray-200 rounded-full">
                            <div className="h-full bg-purple-500 rounded-full" style={{ width: `${pct}%` }} />
                          </div>
                          <span className="text-xs text-gray-400">${c.budget?.toFixed(0) || "0"}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-12">
                <Calendar className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-gray-400">No campaigns with dates</p>
                <button onClick={() => setShowCreate(true)} className="text-sm text-purple-600 mt-2 font-medium hover:underline">Create one →</button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ════════ BUDGET TAB ════════ */}
      {tab === "budget" && (
        <div className="space-y-6">
          {/* Budget Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="card p-6 text-center">
              <DollarSign className="w-8 h-8 text-purple-500 mx-auto mb-2" />
              <p className="text-3xl font-bold text-gray-900">${(s.totalBudget || 0).toLocaleString()}</p>
              <p className="text-sm text-gray-500">Total Budget</p>
            </div>
            <div className="card p-6 text-center">
              <TrendingUp className="w-8 h-8 text-pink-500 mx-auto mb-2" />
              <p className="text-3xl font-bold text-gray-900">${(s.totalSpent || 0).toLocaleString()}</p>
              <p className="text-sm text-gray-500">Total Spent</p>
              <div className="w-full h-2 bg-gray-200 rounded-full mt-3">
                <div className={`h-full rounded-full ${GRADIENT}`} style={{ width: `${s.budgetUtilization || 0}%` }} />
              </div>
              <p className="text-xs text-gray-400 mt-1">{s.budgetUtilization || 0}% utilized</p>
            </div>
            <div className="card p-6 text-center">
              <DollarSign className="w-8 h-8 text-green-500 mx-auto mb-2" />
              <p className="text-3xl font-bold text-green-600">${(s.remaining || 0).toLocaleString()}</p>
              <p className="text-sm text-gray-500">Remaining</p>
            </div>
          </div>

          {/* Per-campaign budget breakdown */}
          <div className="card p-6">
            <h3 className="font-semibold text-gray-900 mb-4">Budget by Campaign</h3>
            {campaigns.length > 0 ? (
              <div className="space-y-4">
                {campaigns.filter((c: any) => c.budget > 0).map((c: any) => {
                  const pct = budgetPct(c.spent || 0, c.budget || 0);
                  return (
                    <div key={c.id} className="flex items-center gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                          <p className="text-sm font-medium text-gray-900 truncate">{c.name}</p>
                          <p className="text-sm text-gray-600">${(c.spent || 0).toFixed(0)} / ${c.budget?.toFixed(0)}</p>
                        </div>
                        <div className="w-full h-2 bg-gray-200 rounded-full">
                          <div className={`h-full rounded-full transition-all ${pct > 90 ? "bg-red-500" : pct > 70 ? "bg-yellow-500" : "bg-purple-500"}`} style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                      <span className="text-xs text-gray-400 w-10 text-right">{pct}%</span>
                    </div>
                  );
                })}
                {campaigns.filter((c: any) => c.budget > 0).length === 0 && (
                  <p className="text-sm text-gray-400 text-center py-4">No campaigns with budget set</p>
                )}
              </div>
            ) : (
              <p className="text-sm text-gray-400 text-center py-8">No campaigns yet</p>
            )}
          </div>

          {/* Average Budget */}
          <div className="card p-6">
            <h3 className="font-semibold text-gray-900 mb-2">Budget Insights</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
              <div className="text-center p-3 bg-purple-50 rounded-xl">
                <p className="text-lg font-bold text-purple-700">${Math.round(s.avgBudget || 0).toLocaleString()}</p>
                <p className="text-xs text-gray-500">Avg Budget</p>
              </div>
              <div className="text-center p-3 bg-green-50 rounded-xl">
                <p className="text-lg font-bold text-green-700">{s.total || 0}</p>
                <p className="text-xs text-gray-500">Total Campaigns</p>
              </div>
              <div className="text-center p-3 bg-blue-50 rounded-xl">
                <p className="text-lg font-bold text-blue-700">{s.active || 0}</p>
                <p className="text-xs text-gray-500">Spending Now</p>
              </div>
              <div className="text-center p-3 bg-pink-50 rounded-xl">
                <p className="text-lg font-bold text-pink-700">{s.completed || 0}</p>
                <p className="text-xs text-gray-500">Completed</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ════════ PERFORMANCE TAB ════════ */}
      {tab === "performance" && (
        <div className="space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <KPI icon={Target} label="Campaigns" value={s.total || 0} color="bg-purple-500" />
            <KPI icon={CheckCircle2} label="Completed" value={s.completed || 0} color="bg-blue-500" sub={`${s.total ? Math.round((s.completed / s.total) * 100) : 0}% completion rate`} />
            <KPI icon={DollarSign} label="Budget Utilization" value={`${s.budgetUtilization || 0}%`} color="bg-pink-500" />
            <KPI icon={TrendingUp} label="Running Now" value={s.runningNow || 0} color="bg-green-500" />
          </div>

          {/* Monthly creation chart */}
          <div className="card p-6">
            <h3 className="font-semibold text-gray-900 mb-4">Campaign Creation Trend</h3>
            {monthlyData.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={monthlyData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Line type="monotone" dataKey="count" stroke="#8B5CF6" strokeWidth={2} dot={{ fill: "#8B5CF6" }} />
                </LineChart>
              </ResponsiveContainer>
            ) : <p className="text-sm text-gray-400 py-12 text-center">No data yet</p>}
          </div>

          {/* Top campaigns by content */}
          <div className="card p-6">
            <h3 className="font-semibold text-gray-900 mb-4">Top Campaigns by Content</h3>
            {campaigns.length > 0 ? (
              <div className="space-y-3">
                {[...campaigns].sort((a: any, b: any) => (b._count?.contents || 0) - (a._count?.contents || 0)).slice(0, 5).map((c: any, i: number) => (
                  <div key={c.id} className="flex items-center gap-3">
                    <span className="text-xs font-bold text-gray-400 w-5">#{i + 1}</span>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">{c.name}</p>
                      <div className="flex items-center gap-2 text-xs text-gray-400 mt-0.5">
                        <span>{c._count?.contents || 0} content</span>
                        <span>· {c._count?.scheduledPosts || 0} posts</span>
                        <span>· {c._count?.adCampaigns || 0} ads</span>
                      </div>
                    </div>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[c.status]}`}>{c.status}</span>
                  </div>
                ))}
              </div>
            ) : <p className="text-sm text-gray-400 text-center py-8">No campaigns yet</p>}
          </div>
        </div>
      )}

      {/* ════════ CREATE MODAL ════════ */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowCreate(false)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900">New Campaign</h2>
              <button onClick={() => setShowCreate(false)} className="p-1 text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={(e) => { e.preventDefault(); createMut.mutate({ ...form, budget: form.budget ? Number(form.budget) : 0, startDate: form.startDate || undefined, endDate: form.endDate || undefined, tags: form.tags || undefined }); }} className="space-y-4">
              <div>
                <label className="label">Campaign Name *</label>
                <input className="input" placeholder="Summer Sale 2025" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
              </div>
              <div>
                <label className="label">Description</label>
                <textarea className="input min-h-[80px]" placeholder="Campaign objectives and strategy..." value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">Type</label>
                  <select className="input" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
                    {Object.entries(TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </div>
                <div>
                  <label className="label">Budget ($)</label>
                  <input type="number" className="input" placeholder="0.00" min="0" step="0.01" value={form.budget} onChange={(e) => setForm({ ...form, budget: e.target.value })} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">Start Date</label>
                  <input type="date" className="input" value={form.startDate} onChange={(e) => setForm({ ...form, startDate: e.target.value })} />
                </div>
                <div>
                  <label className="label">End Date</label>
                  <input type="date" className="input" value={form.endDate} onChange={(e) => setForm({ ...form, endDate: e.target.value })} />
                </div>
              </div>
              <div>
                <label className="label">Tags (comma-separated)</label>
                <input className="input" placeholder="summer, sale, q3" value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} />
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setShowCreate(false)} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-xl transition-colors">Cancel</button>
                <button type="submit" disabled={createMut.isPending} className={`${GRADIENT} text-white px-4 py-2 rounded-xl text-sm font-medium hover:opacity-90 disabled:opacity-50`}>
                  {createMut.isPending ? "Creating..." : "Create Campaign"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ════════ CAMPAIGN DETAIL DRAWER ════════ */}
      {showDetail && detail && (
        <div className="fixed inset-0 bg-black/50 flex justify-end z-50" onClick={() => setShowDetail(null)}>
          <div className="bg-white w-full max-w-2xl h-full overflow-y-auto shadow-xl" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className={`${GRADIENT} p-6`}>
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold text-white">{detail.name}</h2>
                  <p className="text-white/70 text-sm mt-1">{TYPE_LABELS[detail.type] || detail.type} · Created {new Date(detail.createdAt).toLocaleDateString()}</p>
                </div>
                <button onClick={() => setShowDetail(null)} className="p-2 text-white/80 hover:text-white rounded-lg hover:bg-white/10"><X className="w-5 h-5" /></button>
              </div>
              <div className="flex items-center gap-3 mt-4">
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[detail.status]}`}>{detail.status}</span>
                {/* Quick status change */}
                {detail.status !== "active" && <button onClick={() => updateMut.mutate({ id: detail.id, status: "active" })} className="px-3 py-1 rounded-full text-xs font-medium bg-white/20 text-white hover:bg-white/30">→ Activate</button>}
                {detail.status === "active" && <button onClick={() => updateMut.mutate({ id: detail.id, status: "paused" })} className="px-3 py-1 rounded-full text-xs font-medium bg-white/20 text-white hover:bg-white/30">→ Pause</button>}
                {detail.status !== "completed" && <button onClick={() => updateMut.mutate({ id: detail.id, status: "completed" })} className="px-3 py-1 rounded-full text-xs font-medium bg-white/20 text-white hover:bg-white/30">→ Complete</button>}
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Description */}
              {detail.description && (
                <div>
                  <h4 className="text-sm font-medium text-gray-500 mb-1">Description</h4>
                  <p className="text-sm text-gray-700">{detail.description}</p>
                </div>
              )}

              {/* Metrics Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="p-3 bg-purple-50 rounded-xl text-center">
                  <p className="text-lg font-bold text-purple-700">${detail.budget?.toFixed(0) || 0}</p>
                  <p className="text-xs text-gray-500">Budget</p>
                </div>
                <div className="p-3 bg-pink-50 rounded-xl text-center">
                  <p className="text-lg font-bold text-pink-700">${detail.spent?.toFixed(0) || 0}</p>
                  <p className="text-xs text-gray-500">Spent</p>
                </div>
                <div className="p-3 bg-blue-50 rounded-xl text-center">
                  <p className="text-lg font-bold text-blue-700">{detail.timing?.startsIn ? `in ${detail.timing.startsIn}d` : `${detail.timing?.daysActive || 0}d`}</p>
                  <p className="text-xs text-gray-500">{detail.timing?.startsIn ? "Starts In" : "Days Active"}</p>
                </div>
                <div className="p-3 bg-green-50 rounded-xl text-center">
                  <p className="text-lg font-bold text-green-700">{detail.timing?.daysRemaining ?? "∞"}</p>
                  <p className="text-xs text-gray-500">Days Left</p>
                </div>
              </div>

              {/* Timeline Progress */}
              {detail.timing?.progress !== null && detail.timing?.progress !== undefined && detail.timing.progress >= 0 && (
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <h4 className="text-sm font-medium text-gray-500">Timeline Progress</h4>
                    <span className="text-sm font-medium text-purple-700">{detail.timing.progress}%</span>
                  </div>
                  <div className="w-full h-2.5 bg-gray-200 rounded-full">
                    <div className={`h-full rounded-full ${GRADIENT}`} style={{ width: `${detail.timing.progress}%` }} />
                  </div>
                  <div className="flex justify-between text-xs text-gray-400 mt-1">
                    <span>{detail.startDate ? new Date(detail.startDate).toLocaleDateString() : "—"}</span>
                    <span>{detail.endDate ? new Date(detail.endDate).toLocaleDateString() : "—"}</span>
                  </div>
                </div>
              )}

              {/* Budget Progress */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <h4 className="text-sm font-medium text-gray-500">Budget Utilization</h4>
                  <span className="text-sm font-medium text-purple-700">{budgetPct(detail.spent || 0, detail.budget || 0)}%</span>
                </div>
                <div className="w-full h-2.5 bg-gray-200 rounded-full">
                  <div className={`h-full rounded-full ${budgetPct(detail.spent || 0, detail.budget || 0) > 90 ? "bg-red-500" : GRADIENT}`}
                    style={{ width: `${budgetPct(detail.spent || 0, detail.budget || 0)}%` }} />
                </div>
              </div>

              {/* Goal Tracking */}
              <div className="card p-4 border border-purple-100">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-semibold text-gray-700">Goal Tracking</h4>
                  <button
                    onClick={() => saveGoalTargets(detail.id)}
                    disabled={updateMut.isPending}
                    className="px-3 py-1.5 rounded-lg text-xs font-medium bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-50"
                  >
                    {updateMut.isPending ? "Saving..." : "Save Targets"}
                  </button>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
                  {[
                    { key: "impressions", label: "Impressions", value: goalDraft.impressions, actual: detail.performance?.totalImpressions || 0, progress: detail.goalProgress?.impressions },
                    { key: "clicks", label: "Clicks", value: goalDraft.clicks, actual: detail.performance?.totalClicks || 0, progress: detail.goalProgress?.clicks },
                    { key: "conversions", label: "Conversions", value: goalDraft.conversions, actual: detail.performance?.totalConversions || 0, progress: detail.goalProgress?.conversions },
                    { key: "revenue", label: "Revenue", value: goalDraft.revenue, actual: detail.budget || 0, progress: detail.goalProgress?.revenue },
                  ].map((g: any) => (
                    <div key={g.key} className="rounded-lg border border-gray-100 p-2 bg-gray-50">
                      <p className="text-[11px] text-gray-500">{g.label}</p>
                      <input
                        type="number"
                        min="0"
                        className="input mt-1 h-8 text-xs"
                        value={g.value}
                        onChange={(e) => setGoalDraft((prev) => ({ ...prev, [g.key]: e.target.value }))}
                        placeholder="0"
                      />
                      <p className="text-[11px] text-gray-400 mt-1">Actual: {g.actual.toLocaleString()}</p>
                      {g.progress !== null && g.progress !== undefined && (
                        <p className={`text-[11px] font-medium ${g.progress >= 100 ? "text-green-600" : "text-gray-600"}`}>
                          {g.progress}% of target
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Attribution Funnel */}
              <div className="card p-4 border border-blue-100">
                <h4 className="text-sm font-semibold text-gray-700 mb-3">Attribution Funnel</h4>
                <div className="grid grid-cols-3 gap-2 mb-3">
                  <div className="rounded-lg bg-blue-50 p-2 text-center">
                    <p className="text-xs text-gray-500">Impressions</p>
                    <p className="text-sm font-semibold text-blue-700">{(detail.attribution?.funnel?.impressions || 0).toLocaleString()}</p>
                  </div>
                  <div className="rounded-lg bg-purple-50 p-2 text-center">
                    <p className="text-xs text-gray-500">Clicks</p>
                    <p className="text-sm font-semibold text-purple-700">{(detail.attribution?.funnel?.clicks || 0).toLocaleString()}</p>
                  </div>
                  <div className="rounded-lg bg-emerald-50 p-2 text-center">
                    <p className="text-xs text-gray-500">Conversions</p>
                    <p className="text-sm font-semibold text-emerald-700">{(detail.attribution?.funnel?.conversions || 0).toLocaleString()}</p>
                  </div>
                </div>
                <div className="h-44 rounded-xl border border-gray-100 p-2">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={detail.attribution?.channels || []}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="channel" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="clicks" fill="#8B5CF6" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="conversions" fill="#10B981" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* SLA Alerts */}
              <div className="card p-4 border border-amber-100">
                <h4 className="text-sm font-semibold text-gray-700 mb-2">SLA Alerts</h4>
                <div className="space-y-2">
                  {(detail.sla?.alerts || []).map((alert: any, i: number) => (
                    <div
                      key={`${alert.code}-${i}`}
                      className={`rounded-lg px-3 py-2 text-xs border ${
                        alert.severity === "critical" ? "bg-red-50 border-red-200 text-red-700" :
                        alert.severity === "high" ? "bg-amber-50 border-amber-200 text-amber-700" :
                        alert.severity === "medium" ? "bg-blue-50 border-blue-200 text-blue-700" :
                        "bg-green-50 border-green-200 text-green-700"
                      }`}
                    >
                      <p className="font-medium uppercase tracking-wide">{alert.severity}</p>
                      <p>{alert.message}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Content Items */}
              <div>
                <h4 className="text-sm font-medium text-gray-500 mb-2">Content ({detail.contents?.length || 0})</h4>
                {detail.contents?.length > 0 ? (
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {detail.contents.slice(0, 10).map((c: any) => (
                      <div key={c.id} className="flex items-center justify-between p-2 rounded-lg bg-gray-50">
                        <div>
                          <p className="text-sm text-gray-800">{c.title}</p>
                          <p className="text-xs text-gray-400">{c.type} · {c.status}</p>
                        </div>
                        <span className={`px-2 py-0.5 rounded-full text-xs ${STATUS_COLORS[c.status] || "bg-gray-100 text-gray-600"}`}>{c.status}</span>
                      </div>
                    ))}
                  </div>
                ) : <p className="text-xs text-gray-400">No content items yet</p>}
              </div>

              {/* Milestones */}
              <div>
                <h4 className="text-sm font-medium text-gray-500 mb-2">
                  Milestones ({detail.milestoneProgress?.done || 0}/{detail.milestoneProgress?.total || 0})
                </h4>
                {detail.milestones?.length > 0 && (
                  <div className="space-y-2 mb-3">
                    {detail.milestones.map((m: any) => (
                      <div key={m.id} className="flex items-center gap-3 p-2 rounded-lg bg-gray-50">
                        <button onClick={() => toggleMilestoneMut.mutate({ cid: detail.id, mid: m.id, completed: !m.completed })}
                          className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${m.completed ? "bg-green-500 border-green-500" : "border-gray-300 hover:border-purple-400"}`}>
                          {m.completed && <CheckCircle2 className="w-3 h-3 text-white" />}
                        </button>
                        <p className={`text-sm flex-1 ${m.completed ? "line-through text-gray-400" : "text-gray-700"}`}>{m.title}</p>
                        {m.dueDate && <span className="text-xs text-gray-400">{new Date(m.dueDate).toLocaleDateString()}</span>}
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex gap-2">
                  <input className="input flex-1 text-sm" placeholder="Add milestone..." value={milestoneTitle} onChange={(e) => setMilestoneTitle(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && milestoneTitle.trim()) { e.preventDefault(); addMilestoneMut.mutate({ id: detail.id, title: milestoneTitle.trim() }); } }} />
                  <button onClick={() => { if (milestoneTitle.trim()) addMilestoneMut.mutate({ id: detail.id, title: milestoneTitle.trim() }); }}
                    disabled={!milestoneTitle.trim()} className={`${GRADIENT} text-white px-3 py-2 rounded-xl text-sm hover:opacity-90 disabled:opacity-50`}>
                    <Flag className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Notes */}
              <div>
                <h4 className="text-sm font-medium text-gray-500 mb-2">Notes ({detail.notes?.length || 0})</h4>
                {detail.notes?.length > 0 && (
                  <div className="space-y-2 mb-3 max-h-48 overflow-y-auto">
                    {detail.notes.map((n: any) => (
                      <div key={n.id} className="p-3 rounded-lg bg-gray-50">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs font-medium text-gray-600">{n.author?.name || "Unknown"}</span>
                          <span className="text-xs text-gray-400">{new Date(n.createdAt).toLocaleString()}</span>
                        </div>
                        <p className="text-sm text-gray-700">{n.content}</p>
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex gap-2">
                  <input className="input flex-1 text-sm" placeholder="Add a note..." value={noteText} onChange={(e) => setNoteText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && noteText.trim()) { e.preventDefault(); addNoteMut.mutate({ id: detail.id, content: noteText.trim() }); } }} />
                  <button onClick={() => { if (noteText.trim()) addNoteMut.mutate({ id: detail.id, content: noteText.trim() }); }}
                    disabled={!noteText.trim()} className={`${GRADIENT} text-white px-3 py-2 rounded-xl text-sm hover:opacity-90 disabled:opacity-50`}>
                    <StickyNote className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Tags */}
              {detail.tags && (
                <div>
                  <h4 className="text-sm font-medium text-gray-500 mb-2">Tags</h4>
                  <div className="flex flex-wrap gap-2">
                    {detail.tags.split(",").map((t: string, i: number) => (
                      <span key={i} className="px-2 py-1 bg-purple-50 text-purple-600 rounded-lg text-xs font-medium">{t.trim()}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
